import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { allNavItems, canAccess, homeFor } from "@/lib/permissions";
import { AppLayout } from "@/components/layout/AppLayout";
import { FuseauGate } from "@/components/layout/FuseauGate";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AtelierPage from "@/pages/AtelierPage";
import OfPage from "@/pages/OfPage";
import OfDetailPage from "@/pages/OfDetailPage";
import ArchivesPage from "@/pages/ArchivesPage";
import TrsPage from "@/pages/TrsPage";
import AlertesPage from "@/pages/AlertesPage";
import MurPage from "@/pages/MurPage";
import EntreprisesPage from "@/pages/superadmin/EntreprisesPage";
import LignesPage from "@/pages/admin/LignesPage";
import ProduitsPage from "@/pages/admin/ProduitsPage";
import { UnitesPage, QuartsPage, CategoriesArretPage, MotifsRejetPage } from "@/pages/admin/SimpleReferentiels";
import UtilisateursPage from "@/pages/admin/UtilisateursPage";
import EntreprisePage from "@/pages/admin/EntreprisePage";
import EntrepotsPage from "@/pages/admin/EntrepotsPage";
import AidePage from "@/pages/AidePage";
import AnalysePage from "@/pages/AnalysePage";
import InventairePage from "@/pages/InventairePage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import ForbiddenPage from "@/pages/ForbiddenPage";

const RequireAuth = () => {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <Outlet />;
};

const RequireRole = ({ roles, children }) => {
  const { user } = useAuth();
  return canAccess(user.role, roles) ? children : <ForbiddenPage />;
};

const HomeRedirect = () => { const { user } = useAuth(); return <Navigate to={homeFor(user.role)} replace />; };

const PILOTAGE = ["planificateur", "superviseur", "admin", "superadmin"];
const ADMIN = ["admin", "superadmin"];
const ADMIN_PAGES = { "/admin/lignes": LignesPage, "/admin/produits": ProduitsPage, "/admin/unites": UnitesPage, "/admin/quarts": QuartsPage, "/admin/arrets": CategoriesArretPage, "/admin/rejets": MotifsRejetPage, "/admin/utilisateurs": UtilisateursPage, "/admin/entreprise": EntreprisePage, "/admin/entrepots": EntrepotsPage };

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/mur" element={<RequireRole roles={PILOTAGE}><FuseauGate><MurPage /></FuseauGate></RequireRole>} />
            <Route element={<AppLayout />}>
              <Route index element={<HomeRedirect />} />
              <Route path="/dashboard" element={<RequireRole roles={PILOTAGE}><DashboardPage /></RequireRole>} />
              <Route path="/atelier" element={<AtelierPage />} />
              <Route path="/aide" element={<AidePage />} />
              <Route path="/inventaire" element={<RequireRole roles={["planificateur", "magasinier", "admin", "superadmin"]}><InventairePage /></RequireRole>} />
              <Route path="/analyse" element={<RequireRole roles={PILOTAGE}><AnalysePage /></RequireRole>} />
              <Route path="/analyses" element={<Navigate to="/analyse" replace />} />
              <Route path="/exports" element={<Navigate to="/analyse?tab=vue" replace />} />
              <Route path="/of" element={<RequireRole roles={PILOTAGE}><OfPage /></RequireRole>} />
              <Route path="/of/:id" element={<RequireRole roles={PILOTAGE}><OfDetailPage /></RequireRole>} />
              <Route path="/archives" element={<RequireRole roles={PILOTAGE}><ArchivesPage /></RequireRole>} />
              <Route path="/trs" element={<RequireRole roles={PILOTAGE}><TrsPage /></RequireRole>} />
              <Route path="/alertes" element={<RequireRole roles={PILOTAGE}><AlertesPage /></RequireRole>} />
              <Route path="/superadmin/entreprises" element={<RequireRole roles={["superadmin"]}><EntreprisesPage /></RequireRole>} />
              {Object.entries(ADMIN_PAGES).map(([p, C]) => <Route key={p} path={p} element={<RequireRole roles={ADMIN}><C /></RequireRole>} />)}
              <Route path="/admin" element={<Navigate to="/admin/lignes" replace />} />
              {allNavItems.filter((i) => !["/dashboard", "/atelier", "/of", "/archives", "/analyses", "/exports", "/trs", "/alertes", "/superadmin/entreprises", "/admin", "/aide", "/analyse", "/inventaire"].includes(i.to) && !ADMIN_PAGES[i.to]).map((i) => (
                <Route key={i.to} path={i.to} element={<RequireRole roles={i.roles}><PlaceholderPage /></RequireRole>} />
              ))}
              <Route path="*" element={<HomeRedirect />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors theme="dark" />
    </AuthProvider>
  );
}

export default App;
